import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import Hero from '../components/ui/Hero';
import Button from '../components/ui/Button';
import AnimatedButton from '../components/ui/AnimatedButton';
import FaqCTA from '../components/ui/FaqCTA';
import { getServiceCategories } from '../services/api';
import type { ServiceCategory } from '../types/api';
import maincoeurImage from '../assets/maincoeur.png';
import femmeenceinteImage from '../assets/femmeenceinteemoji.png';
import fleurcerisierImage from '../assets/fleurcerisieremoticone.png';
import accueilBebeImage from '../assets/accueil_bebe.png';
import mamanIconeImage from '../assets/maman_icone.png';
import bambinIconeImage from '../assets/bambin_icone.png';
import { useScrollReveal } from '../hooks/useScrollReveal';

interface FeatureHighlightProps {
  index: number;
  title: string;
  caption: string;
  image: string;
  alt: string;
}

const FeatureHighlight: React.FC<FeatureHighlightProps> = ({ index, title, caption, image, alt }) => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${index * 0.12}s` }}
      className={`text-center transition-all duration-700 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
    >
      <div className="w-32 h-32 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <img src={image} alt={alt} loading="lazy" className="w-28 h-28 object-contain" />
      </div>
      <h3 className="font-playfair text-2xl font-bold text-neutral-ink mb-4">{title}</h3>
      <p className="font-nunito text-neutral-body text-base md:text-lg leading-relaxed max-w-xs mx-auto">
        {caption}
      </p>
    </div>
  );
};

interface CategoryCardProps {
  category: ServiceCategory;
  index: number;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, index }) => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });

  // Mapping des images par slug de catégorie
  const categoryImages: Record<string, string> = {
    'pour-bebe': accueilBebeImage,
    'pour-moman': mamanIconeImage,
    'pour-bambin': bambinIconeImage
  };

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${index * 0.15}s` }}
      className={`bg-white rounded-2xl p-8 text-center shadow-soft transition-all duration-700 ease-out transform ${isVisible ? 'opacity-100 translate-y-0 rotate-0 shadow-lg' : 'opacity-0 translate-y-10 rotate-3'} hover:shadow-2xl hover:-translate-y-2 hover:scale-105`}
    >
      <div className="w-48 h-48 flex items-center justify-center mx-auto mb-5">
        <img src={categoryImages[category.slug] || accueilBebeImage} alt={category.name} loading="lazy" className="w-full h-full object-contain" />
      </div>
      <h3 className="font-playfair text-2xl font-bold text-neutral-ink mb-4">
        {category.name}
      </h3>
      <p className="font-nunito text-neutral-body text-base md:text-lg mb-6 whitespace-pre-line leading-relaxed">
        {category.description}
      </p>
      <Button 
        variant="outline" 
        size="md" 
        to={`/services#${category.slug}`}
        className="w-full group relative overflow-hidden border-2 border-brand hover:border-brand-dark transition-all duration-300 hover:scale-105 hover:shadow-lg active:scale-95 !bg-transparent hover:!bg-transparent"
      >
        {/* Effet de remplissage liquide */}
        <span className="absolute inset-0 bg-brand opacity-0 translate-y-full group-hover:opacity-100 group-hover:animate-liquid-fill transition-opacity duration-300"></span>
        
        <span className="relative z-10 text-brand group-hover:text-white transition-colors duration-500 flex items-center justify-center gap-2 font-semibold">
          <span>Voir les services</span>
          <svg className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </span>
      </Button>
    </div>
  );
};

const Home: React.FC = () => {
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getServiceCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const featureHighlights = [
    {
      title: 'Écoute et bienveillance',
      caption: 'Des échanges authentiques, une écoute active et des conseils adaptés à vos besoins uniques.',
      image: maincoeurImage,
      alt: 'Icône mains en cœur'
    },
    {
      title: 'Douceur et bien être',
      caption: 'Des rituels sensoriels pour apaiser le corps et l’esprit à chaque étape de la maternité.',
      image: femmeenceinteImage,
      alt: 'Icône maman sereine'
    },
    {
      title: 'Cocon apaisant',
      caption: 'Un lieu chaleureux pensé comme un refuge pour ralentir, respirer et se reconnecter.',
      image: fleurcerisierImage,
      alt: 'Icône fleur de cerisier'
    }
  ];

  const { ref: whyHeaderRef, isVisible: whyHeaderVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });
  const { ref: whyGridRef, isVisible: whyGridVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });
  const { ref: whyCtaRef, isVisible: whyCtaVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.25 });
  const { ref: specialtiesHeaderRef, isVisible: specialtiesHeaderVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });
  const { ref: ctaSectionRef, isVisible: ctaSectionVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });

  const localBusinessJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'DaySpa',
    '@id': 'https://douxcemoman.fr/#business',
    name: 'Doux ce Moman',
    description: 'Centre de bien-être périnatal près de Besançon, à Geneuille (25). Massages femmes enceintes, spa bébé, bain enveloppé et ateliers parent-enfant.',
    url: 'https://douxcemoman.fr',
    logo: {
      '@type': 'ImageObject',
      url: 'https://douxcemoman.fr/logo.png',
      width: 200,
      height: 200,
    },
    image: {
      '@type': 'ImageObject',
      url: 'https://douxcemoman.fr/accueil_image.png',
      width: 1200,
      height: 630,
    },
    telephone: '+33628979907',
    email: 'contact@douxcemoman.fr',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '32 Rue Lyautey',
      addressLocality: 'Geneuille',
      postalCode: '25870',
      addressRegion: 'Bourgogne-Franche-Comté',
      addressCountry: 'FR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 47.3086,
      longitude: 6.0758,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Saturday',
        opens: '09:00',
        closes: '18:00',
        // Note: "Sur appel uniquement" — no invalid 'description' property
      },
    ],
    priceRange: '€€',
    sameAs: [
      'https://www.instagram.com/douxcemoman/',
      'https://www.facebook.com/douxcemoman/',
    ],
    areaServed: [
      { '@type': 'City', name: 'Besançon', sameAs: 'https://fr.wikipedia.org/wiki/Besan%C3%A7on' },
      { '@type': 'City', name: 'Geneuille' },
      { '@type': 'AdministrativeArea', name: 'Doubs' },
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Services bien-être périnatal',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Massage prénatal' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Spa bébé' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Bain enveloppé' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Atelier bain enveloppé' } },
      ],
    },
  };

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': 'https://douxcemoman.fr/#website',
    name: 'Doux ce Moman',
    url: 'https://douxcemoman.fr',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://douxcemoman.fr/services?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const personsJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Marie Amet',
      jobTitle: 'Praticienne bien-être périnatal',
      worksFor: { '@id': 'https://douxcemoman.fr/#business' },
      url: 'https://douxcemoman.fr/a-propos',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Morgane Darbon',
      jobTitle: 'Praticienne bien-être périnatal',
      worksFor: { '@id': 'https://douxcemoman.fr/#business' },
      url: 'https://douxcemoman.fr/a-propos',
    },
  ];

  return (
    <article className="bg-neutral-offwhite">
      <SEOHead
        title="Spa Bébé & Massage Prénatal"
        description="Centre de bien-être périnatal à Geneuille (25). Massages femmes enceintes, spa bébé, bain enveloppé, ateliers parent-enfant. Prenez rendez-vous en ligne."
        path="/"
        jsonLd={[localBusinessJsonLd, websiteJsonLd, ...personsJsonLd]}
      />
      {/* Hero Section */}
      <Hero />
      
      {/* Pourquoi nous choisir */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div
            ref={whyHeaderRef}
            className={`text-center mb-12 transition-all duration-700 ease-out transform ${whyHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <div className="inline-flex items-center gap-3 px-6 py-2 rounded-full bg-white/70 backdrop-blur-sm border border-brand/10 shadow-soft mb-4">
              <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
              <span className="font-nunito text-sm uppercase tracking-[0.3em] text-brand">Maison du bien-être</span>
            </div>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-neutral-ink mb-4">
              Pourquoi choisir Doux ce Moman ?
            </h2>
            <p className="font-nunito text-neutral-body max-w-2xl mx-auto text-lg md:text-xl leading-relaxed">
              Une expérience sensorielle pensée pour envelopper les futures mamans et leurs bébés d'une douceur infinie.
            </p>
          </div>

          <div
            ref={whyGridRef}
            className={`grid grid-cols-1 md:grid-cols-3 gap-8 mb-14 transition-transform duration-700 ease-out ${whyGridVisible ? 'scale-100' : 'scale-95'} `}
          >
            {featureHighlights.map((feature, index) => (
              <FeatureHighlight key={feature.title} index={index} {...feature} />
            ))}
          </div>

          <div
            ref={whyCtaRef}
            className={`text-center transition-all duration-700 ease-out transform ${whyCtaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <AnimatedButton 
              to="/services"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              }
            >
              Découvrir nos services
            </AnimatedButton>
          </div>
        </div>
      </section>

      {/* Nos catégories */}
      <section className="py-16 bg-gradient-to-b from-neutral-offwhite to-white">
        <div className="container mx-auto px-4">
          <div
            ref={specialtiesHeaderRef}
            className={`text-center mb-12 transition-all duration-700 ease-out transform ${specialtiesHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <div className="inline-flex items-center gap-3 px-6 py-2 rounded-full bg-white shadow-soft mb-4">
              <span className="text-2xl">✨</span>
              <span className="font-nunito text-sm uppercase tracking-[0.3em] text-brand">Découvrez</span>
            </div>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-neutral-ink mb-4">
              Nos Spécialités
            </h2>
            <p className="font-nunito text-neutral-body max-w-2xl mx-auto text-lg md:text-xl leading-relaxed">
              Des services adaptés à chaque étape de votre parcours
            </p>
          </div>
          
          {loading ? (
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
              <p className="font-nunito text-neutral-body text-base mt-2">Chargement des spécialités...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {categories.map((category, index) => (
                <CategoryCard key={category.id} category={category} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-b from-white to-accent-sage/10">
        <div className="container mx-auto px-4">
          <div
            ref={ctaSectionRef}
            className={`max-w-6xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-700 ease-out transform ${ctaSectionVisible ? 'opacity-100 translate-y-0 shadow-2xl' : 'opacity-0 translate-y-12'}`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Image Gallery */}
              <div className="relative h-[400px] lg:h-auto overflow-hidden">
                <div className="grid grid-cols-2 grid-rows-2 gap-2 h-full p-2">
                  {/* Main large image - top left spanning 2 rows */}
                  <div className="row-span-2 overflow-hidden rounded-xl group">
                    <img 
                      src="/acceuil.jpg"
                      alt="Espace d'accueil chaleureux"
                      loading="lazy"
                      className={`w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 ${ctaSectionVisible ? 'scale-100' : 'scale-110'}`}
                    />
                  </div>
                  {/* Top right image */}
                  <div className="overflow-hidden rounded-xl group">
                    <img 
                      src="/spa_bebe.jpg"
                      alt="Spa bébé - moment de douceur"
                      loading="lazy"
                      className={`w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 ${ctaSectionVisible ? 'scale-100' : 'scale-110'}`}
                    />
                  </div>
                  {/* Bottom right image */}
                  <div className="overflow-hidden rounded-xl group">
                    <img 
                      src="/spa_bebe2.jpg"
                      alt="Spa bébé - soins adaptés"
                      loading="lazy"
                      className={`w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 ${ctaSectionVisible ? 'scale-100' : 'scale-110'}`}
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 md:p-12 flex flex-col justify-center bg-gradient-to-br from-white to-accent-sage/5">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand/10 w-fit mb-6">
                  <span className="text-2xl">✨</span>
                  <span className="font-nunito text-sm uppercase tracking-wider text-brand font-semibold">Réservez maintenant</span>
                </div>
                
                <h2 className="font-playfair text-3xl md:text-4xl font-bold text-neutral-ink mb-4 leading-tight">
                  Offrez-vous un moment de bien-être
                </h2>
                
                <p className="font-nunito text-neutral-body text-base md:text-lg leading-relaxed mb-8">
                  Réservez dès maintenant votre séance et laissez-vous porter par nos soins experts. 
                  Votre bien-être et celui de votre bébé sont notre priorité.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <AnimatedButton 
                    to="/reservation"
                    icon={
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    }
                  >
                    Prendre rendez-vous
                  </AnimatedButton>
                  <AnimatedButton 
                    to="/bons-cadeaux"
                    icon={<span className="text-2xl">🎁</span>}
                  >
                    Offrir un bon cadeau
                  </AnimatedButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ CTA */}
      <section className="py-12 bg-accent-sage/10">
        <div className="container mx-auto px-4">
          <FaqCTA />
        </div>
      </section>
    </article>
  );
};

export default Home;